import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function SharePointDocumentSource({ botId }: { botId: string }) {
  const cache = useQueryClient();
  const { data, error } = useQuery<{ available: boolean; reason?: string }>({
    queryKey: ["/api/sharepoint-documents/availability"],
    staleTime: 0,
    retry: false,
  });
  const [siteUrl, setSiteUrl] = useState("");
  const [library, setLibrary] = useState("");
  const [documentPath, setDocumentPath] = useState("");
  const [readerRoles, setReaderRoles] = useState("");
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  async function perform(refresh: boolean) {
    setBusy(true); setNotice("");
    try {
      if (!refresh && !readerRoles.split(",").some(r => r.trim()))
        throw new Error("Enter at least one authorized reader role.");
      const response = await apiRequest("POST", refresh
        ? `/api/chatbots/${botId}/sharepoint-document/${sourceId}/refresh`
        : `/api/chatbots/${botId}/sharepoint-document`,
      refresh ? undefined : {
        siteUrl, library, documentPath,
        readers: { allowedRoles: readerRoles.split(",").map(r => r.trim()).filter(Boolean) },
      });
      const result = await response.json();
      setSourceId(result.id);
      setNotice(refresh
        ? "Processed and submitted for review. Approval and publication are separate actions."
        : "Document identity bound using metadata only. No content downloaded.");
      await cache.invalidateQueries({ queryKey: ["/api/chatbots", botId, "knowledge"] });
      await cache.invalidateQueries({ queryKey: ["/api/content-governance"] });
    } catch (e) { setNotice(e instanceof Error ? e.message : "The operation failed."); }
    finally { setBusy(false); }
  }
  return <section aria-label="Private SharePoint document" className="border rounded-lg p-4 space-y-3">
    <h3 className="font-semibold">Private SharePoint document</h3>
    {!data?.available
      ? <p role="status" className="text-sm text-muted-foreground">
        {error ? "The single-document API is not active." : data?.reason || "Checking feature availability…"}
      </p>
      : <>
        <p className="text-sm text-muted-foreground">
          Bind one DOCX. Readers must sign in. Refresh downloads only the bound item and requires review again.
          While bound, the bot answers only from matching approved document evidence; missing evidence is never sent to AI generation.
        </p>
        {[
          ["Site URL", siteUrl, setSiteUrl, "https://example.sharepoint.com/sites/Team"],
          ["Library name", library, setLibrary, "Documents"],
          ["Relative DOCX path", documentPath, setDocumentPath, "Folder/Document.docx"],
          ["Authorized reader roles (comma-separated)", readerRoles, setReaderRoles, "DocumentReader"],
        ].map(([label, value, update, placeholder], index) => <label key={index} className="block text-sm">
          {label as string}
          <Input value={value as string} disabled={!!sourceId || busy}
            placeholder={placeholder as string}
            onChange={e => (update as (value: string) => void)(e.target.value)} />
        </label>)}
        <Button disabled={busy || !siteUrl || !library || !documentPath || !readerRoles}
          onClick={() => perform(!!sourceId)}>
          {busy ? "Working…" : sourceId ? "Download bound revision for review" : "Bind document metadata"}
        </Button>
      </>}
    {notice && <p role="status" className="text-sm">{notice}</p>}
  </section>;
}